import axios from 'axios';
import { LoginCredentials, AuthResponse } from '../types/auth';

const API_BASE_URL = (typeof window !== 'undefined' ? `${window.location.origin}/api` : ((process.env.REACT_APP_API_URL as string | undefined) || '/api'));

// Crea un'istanza di axios con configurazione base
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere il token di autenticazione alle richieste
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire le risposte e gli errori
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token scaduto o non valido, rimuovi dal localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Servizi di autenticazione
export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  me: async (): Promise<AuthResponse> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async (): Promise<void> => {
    // Rimuovi i dati dal localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  },

  changePassword: async (payload: { currentPassword: string; newPassword: string }) => {
    const response = await api.put('/auth/change-password', payload);
    return response.data;
  },
  changeEmail: async (payload: { email: string }) => {
    const response = await api.put('/auth/change-email', payload);
    return response.data;
  },
  verifyPassword: async (password: string) => {
    const response = await api.post('/auth/verify-password', { password });
    return response.data;
  },
};

// Servizi per allergeni
export const allergenService = {
  getAll: async () => {
    const response = await api.get('/allergens');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/allergens/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/allergens', data);
    return response.data;
  },

  update: async (id: number, data: any) => {
    const response = await api.put(`/allergens/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/allergens/${id}`);
    return response.data;
  },
};

// Servizi per ingredienti
export const ingredientService = {
  getAll: async () => {
    const response = await api.get('/ingredients');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/ingredients/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/ingredients', data);
    return response.data;
  },

  update: async (id: number, data: any) => {
    const response = await api.put(`/ingredients/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/ingredients/${id}`);
    return response.data;
  },
};

// Servizi per prodotti
export const productService = {
  getAll: async () => {
    const response = await api.get('/products');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  getByCategory: async (categoryId: number) => {
    const response = await api.get(`/products/category/${categoryId}`);
    return response.data;
  },

  getByIds: async (ids: number[]) => {
    const response = await api.get(`/products/by-ids`, { params: { ids: ids.join(',') } });
    return response.data;
  },

  search: async (query: string) => {
    const response = await api.get(`/products/search/${encodeURIComponent(query)}`);
    return response.data;
  },

  create: async (data: any) => {
    const config = data instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response = await api.post('/products', data, config);
    return response.data;
  },

  update: async (id: number, data: any) => {
    const config = data instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response = await api.put(`/products/${id}`, data, config);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  bulkUpdate: async (data: {
    product_ids: number[];
    add_allergen_ids?: number[];
    remove_allergen_ids?: number[];
    add_ingredient_ids?: number[];
    remove_ingredient_ids?: number[];
    category_id?: number;
    is_available?: boolean;
  }) => {
    const response = await api.patch('/products/bulk', data);
    return response.data;
  },
};

// Servizi per categorie
export const categoryService = {
  getAll: async (tree = false) => {
    const response = await api.get(`/categories${tree ? '?tree=true' : ''}`);
    return response.data;
  },

  getPublic: async () => {
    const response = await api.get('/categories/public');
    return response.data;
  },

  getConcatenated: async () => {
    const response = await api.get('/categories?concatenated=true');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  create: async (data: any) => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  update: async (id: number, data: any) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

// Servizio per gestire i dati dell'attività
export const businessService = {
  get: async () => {
    const response = await api.get('/business');
    return response.data;
  },

  update: async (data: any) => {
    const response = await api.put('/business', data);
    return response.data;
  },

  uploadLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post('/business/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getMenuQr: async (): Promise<Blob> => {
    const response = await api.get('/business/qrcode', { responseType: 'blob' });
    return response.data;
  },
};

// Servizi per utenti (admin)
export const userService = {
  create: async (data: { username: string; email: string; password: string; role?: 'admin' | 'cook' | 'waiter' }) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  list: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  setActive: async (id: number, is_active: boolean) => {
    const response = await api.put(`/users/${id}/status`, { is_active });
    return response.data;
  },
  updateEmail: async (id: number, email: string) => {
    const response = await api.put(`/users/${id}/email`, { email });
    return response.data;
  },
  updateRole: async (id: number, role: 'admin' | 'cook' | 'waiter') => {
    const response = await api.put(`/users/${id}/role`, { role });
    return response.data;
  },
  updatePassword: async (id: number, newPassword: string) => {
    const response = await api.put(`/users/${id}/password`, { newPassword });
    return response.data;
  },
};

// Servizi per menu personalizzati
export const customMenuService = {
  getAll: async () => {
    const response = await api.get('/custom-menus');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/custom-menus/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/custom-menus', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/custom-menus/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/custom-menus/${id}`);
    return response.data;
  },
};

export const qrCodeService = {
  getAll: async () => {
    const response = await api.get('/qr-codes');
    return response.data;
  },
  create: async (data: { name: string; destination_url: string }) => {
    const response = await api.post('/qr-codes', data);
    return response.data;
  },
  update: async (id: number, data: { name: string; destination_url: string }) => {
    const response = await api.put(`/qr-codes/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/qr-codes/${id}`);
    return response.data;
  },
  toggle: async (id: number) => {
    const response = await api.patch(`/qr-codes/${id}/toggle`);
    return response.data;
  },
  getImage: async (uuid: string): Promise<Blob> => {
    const response = await api.get(`/qr-codes/${uuid}/image`, { responseType: 'blob' });
    return response.data;
  },
};

export const roomService = {
  getAll: async () => { const r = await api.get('/rooms'); return r.data; },
  create: async (data: { name: string; sort_order?: number }) => { const r = await api.post('/rooms', data); return r.data; },
  update: async (id: number, data: any) => { const r = await api.put(`/rooms/${id}`, data); return r.data; },
  delete: async (id: number) => { const r = await api.delete(`/rooms/${id}`); return r.data; },
};

export const tableService = {
  getAll: async () => { const r = await api.get('/dining-tables'); return r.data; },
  getByRoom: async (roomId: number) => { const r = await api.get(`/dining-tables/room/${roomId}`); return r.data; },
  create: async (data: { room_id: number; name: string; seats?: number; x?: number; y?: number }) => { const r = await api.post('/dining-tables', data); return r.data; },
  update: async (id: number, data: any) => { const r = await api.put(`/dining-tables/${id}`, data); return r.data; },
  delete: async (id: number) => { const r = await api.delete(`/dining-tables/${id}`); return r.data; },
  merge: async (primary_id: number, secondary_id: number) => { const r = await api.post('/dining-tables/merge', { primary_id, secondary_id }); return r.data; },
  unmerge: async (secondary_id: number) => { const r = await api.post('/dining-tables/unmerge', { secondary_id }); return r.data; },
};

export const publicTableService = {
  getTable: async (uuid: string) => { const r = await api.get(`/public/table/${uuid}`); return r.data; },
  openTable: async (uuid: string) => {
    try { const r = await api.post(`/public/table/${uuid}/open`); return r.data; }
    catch (e: any) { return e.response?.data || { success: false, message: 'Errore' }; }
  },
  addItem: async (uuid: string, data: { product_id?: number; product_name: string; product_price: number; quantity?: number; notes?: string; course?: number; course_name?: string }) => {
    try { const r = await api.post(`/public/table/${uuid}/items`, data); return r.data; }
    catch (e: any) { return e.response?.data || { success: false, message: 'Errore' }; }
  },
};

export const tableQrService = {
  downloadQr: async (tableId: number, tableName: string) => {
    const r = await api.get(`/dining-tables/${tableId}/qr`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `tavolo-${tableName}.png`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.URL.revokeObjectURL(url);
  },
};

export const orderService = {
  getActive: async () => { const r = await api.get('/orders/active'); return r.data; },
  getAll: async (params?: { date?: string; limit?: number }) => { const r = await api.get('/orders', { params }); return r.data; },
  getById: async (id: number) => { const r = await api.get(`/orders/${id}`); return r.data; },
  getStats: async (days = 30) => { const r = await api.get('/orders/stats', { params: { days } }); return r.data; },
  create: async (data: { table_id: number; notes?: string }) => {
    try { const r = await api.post('/orders', data); return r.data; }
    catch (e: any) { return e.response?.data || { success: false }; }
  },
  closeOrder: async (id: number, payload?: { covers?: number; cover_price?: number; adjustment?: number; final_total?: number }) => { const r = await api.put(`/orders/${id}/close`, payload || {}); return r.data; },
  cancelOrder: async (id: number) => { const r = await api.put(`/orders/${id}/cancel`); return r.data; },
  requestBill: async (id: number) => { const r = await api.put(`/orders/${id}/request-bill`); return r.data; },
  cancelBill: async (id: number) => { const r = await api.put(`/orders/${id}/cancel-bill`); return r.data; },
  addItem: async (orderId: number, data: { product_id?: number; product_name: string; product_price: number; quantity?: number; notes?: string; course?: number; course_name?: string }) => {
    const r = await api.post(`/orders/${orderId}/items`, data); return r.data;
  },
  sendOrder: async (orderId: number) => { const r = await api.post(`/orders/${orderId}/send`); return r.data; },
  advanceCourse: async (orderId: number, course: number, status: string) => { const r = await api.put(`/orders/${orderId}/courses/${course}`, { status }); return r.data; },
  updateItemQty: async (itemId: number, quantity: number) => { const r = await api.put(`/orders/items/${itemId}/qty`, { quantity }); return r.data; },
  updateItemStatus: async (itemId: number, status: string) => { const r = await api.put(`/orders/items/${itemId}/status`, { status }); return r.data; },
  removeItem: async (itemId: number) => { const r = await api.delete(`/orders/items/${itemId}`); return r.data; },
};

export const statsService = {
  trackMenuView: async () => {
    const response = await api.post('/stats/menu-view');
    return response.data;
  },
  /** Heartbeat tempo di permanenza; con `beacon` usa sendBeacon (affidabile alla chiusura della pagina) */
  pingMenu: (sessionId: string, beacon = false) => {
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const body = new Blob([JSON.stringify({ session_id: sessionId })], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/stats/menu-ping`, body);
      return Promise.resolve();
    }
    return api.post('/stats/menu-ping', { session_id: sessionId }).then(() => undefined);
  },
  getStats: async () => {
    const response = await api.get('/stats');
    return response.data;
  },
  getAnalytics: async (days = 30) => {
    const response = await api.get('/stats/analytics', { params: { days } });
    return response.data;
  },
};

export const popupService = {
  getAll: async () => {
    const response = await api.get('/popups');
    return response.data;
  },
  getActive: async () => {
    const response = await api.get('/popups/active');
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/popups/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/popups', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/popups/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/popups/${id}`);
    return response.data;
  },
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/popups/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  uploadVideo: async (file: File) => {
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.post('/popups/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default api;